/**
 * Eclipse bloom: a TSL mip-chain bloom node in the Call of Duty: Advanced Warfare style (Jimenez 2014), used by
 * the post RenderPipeline (style guide §3.5). Why not three's BloomNode: its blur kernels are fixed in texels of
 * each mip, so changing its resolution changes the halo size and High and Medium would not match; it has no
 * anti-firefly filter (sub-pixel glints and nav lights flicker); and it allocates strings every frame.
 *
 * Chain: a 13-tap prefilter with a per-tap soft threshold and a Karis average reads the full-res HDR frame into
 * level 0 (1/2 of the frame on `full`, 1/4 on `half`), 13-tap downsamples go down to 1/128, then 3×3 tent
 * upsamples climb back, adding each level with its weight (three's BloomNode `radius` semantics, see tiers.ts).
 * The node's value is the finest upsampled level (linear HDR, not yet scaled by strength).
 * Per-frame work is allocation-free: render targets resize only when the drawing buffer or the mode changes.
 */
import {
  HalfFloatType,
  NodeMaterial,
  NodeUpdateType,
  QuadMesh,
  RenderTarget,
  RendererUtils,
  TempNode,
  Vector2,
  type Node,
  type NodeBuilder,
  type NodeFrame,
  type TextureNode,
  type UniformNode,
} from 'three/webgpu'
import { Fn, context, float, luminance, max, passTexture, smoothstep, texture, uniform, uv, vec2, vec4 } from 'three/tsl'
import { BLOOM_LEVELS, type BloomMode } from './data'
import { bloomLevelSize } from './tiers'

type RendererState = ReturnType<typeof RendererUtils.resetRendererState>
type Vec2Node = Node<'vec2'>
type Vec3Node = Node<'vec3'>
type FloatNode = Node<'float'>

export interface BloomUniforms {
  readonly threshold: UniformNode<'float', number>
  readonly smoothWidth: UniformNode<'float', number>
  /** Tent upsample footprint in source texels. */
  readonly spread: UniformNode<'float', number>
}

type Tap = readonly [number, number]

/** 13-tap pattern (offsets in source texels): 4 inner taps and a 3×3 outer grid at spacing 2. */
const INNER: readonly Tap[] = [
  [-1, 1],
  [1, 1],
  [-1, -1],
  [1, -1],
]
const OUTER: readonly Tap[] = [
  [-2, 2],
  [0, 2],
  [2, 2],
  [-2, 0],
  [0, 0],
  [2, 0],
  [-2, -2],
  [0, -2],
  [2, -2],
]
/** The four 2×2 corner boxes of the outer grid, as indices into OUTER. */
const CORNER_BOXES: readonly (readonly [number, number, number, number])[] = [
  [0, 1, 3, 4],
  [1, 2, 4, 5],
  [3, 4, 6, 7],
  [4, 5, 7, 8],
]
/** Box weights of the 13-tap filter: the inner box 0.5, each corner box 0.125 (sum 1). */
const INNER_WEIGHT = 0.5
const CORNER_WEIGHT = 0.125
/** 3×3 tent: x, y offset and weight (weights sum to 16). */
const TENT: readonly (readonly [number, number, number])[] = [
  [-1, -1, 1],
  [0, -1, 2],
  [1, -1, 1],
  [-1, 0, 2],
  [0, 0, 4],
  [1, 0, 2],
  [-1, 1, 1],
  [0, 1, 2],
  [1, 1, 1],
]

type Sampler = (at: Vec2Node) => Vec3Node

function makeTarget(name: string): RenderTarget {
  const rt = new RenderTarget(1, 1, { depthBuffer: false, type: HalfFloatType })
  rt.texture.name = name
  rt.texture.generateMipmaps = false
  return rt
}

/**
 * 13-tap downsample of `sample` around `center` with `texel` spacing. `karis` weights each box by
 * 1 / (1 + luma) so a single hot texel cannot dominate its neighbourhood (no fireflies).
 */
function thirteenTap(sample: Sampler, center: Vec2Node, texel: Vec2Node, karis: boolean): Vec3Node {
  const at = (o: Tap): Vec3Node => sample(center.add(texel.mul(vec2(o[0], o[1]))))
  const inner = INNER.map(at)
  const outer = OUTER.map(at)
  const boxes: { c: Vec3Node; w: number }[] = [{ c: inner[0].add(inner[1]).add(inner[2]).add(inner[3]).mul(0.25), w: INNER_WEIGHT }]
  for (const b of CORNER_BOXES) {
    boxes.push({ c: outer[b[0]].add(outer[b[1]]).add(outer[b[2]]).add(outer[b[3]]).mul(0.25), w: CORNER_WEIGHT })
  }
  if (!karis) {
    let sum: Vec3Node = boxes[0].c.mul(boxes[0].w)
    for (let i = 1; i < boxes.length; i++) sum = sum.add(boxes[i].c.mul(boxes[i].w))
    return sum
  }
  let sum: Vec3Node | null = null
  let wsum: FloatNode | null = null
  for (const b of boxes) {
    const w = float(b.w).div(luminance(b.c).add(1))
    sum = sum === null ? b.c.mul(w) : sum.add(b.c.mul(w))
    wsum = wsum === null ? w : wsum.add(w)
  }
  return (sum as Vec3Node).div(max(wsum as FloatNode, 1e-5))
}

function tent(sample: Sampler, center: Vec2Node, texel: Vec2Node): Vec3Node {
  let sum: Vec3Node | null = null
  for (const t of TENT) {
    const s = sample(center.add(texel.mul(vec2(t[0], t[1])))).mul(t[2] / 16)
    sum = sum === null ? s : sum.add(s)
  }
  return sum as Vec3Node
}

/** 3×3 tent upsample of a texture node at `texel` spacing (the composite uses it on the bloom output). */
export function tentSample(tex: TextureNode, center: Vec2Node, texel: Vec2Node): Vec3Node {
  return tent((at) => tex.sample(at).rgb, center, texel)
}

interface DownPass {
  readonly material: NodeMaterial
  readonly texel: UniformNode<'vec2', Vector2>
}

interface UpPass {
  readonly material: NodeMaterial
  readonly prev: TextureNode
  readonly texel: UniformNode<'vec2', Vector2>
  /** x = weight of this level, y = weight of the coarser input (Vector2 fields: storing doubles never allocates). */
  readonly weights: UniformNode<'vec2', Vector2>
}

export class EclipseBloomNode extends TempNode<'vec4'> {
  readonly inputNode: TextureNode
  readonly uniforms: BloomUniforms
  /** Texel size of the output level, for a tent upsample in the composite. */
  readonly outputTexel: UniformNode<'vec2', Vector2>
  private readonly karis: boolean
  private mode: BloomMode = 'full'
  private levels: number = BLOOM_LEVELS
  private readonly weights = new Float32Array(BLOOM_LEVELS)
  private readonly down: RenderTarget[] = []
  private readonly up: RenderTarget[] = []
  private readonly quad = new QuadMesh(new NodeMaterial())
  private readonly size = new Vector2()
  private width = 0
  private height = 0
  private sizedMode: BloomMode | null = null
  private state: RendererState | undefined = undefined
  // One material per pass with its own fixed textures and uniforms: the per-frame loop only binds targets and
  // draws, so nothing changes between passes (a changed texture binding rebuilds a bind group, per pass).
  private readonly inTexel = uniform(new Vector2())
  private prefilter: NodeMaterial | null = null
  private readonly downPasses: DownPass[] = []
  private readonly upPasses: UpPass[] = []
  private readonly output: TextureNode

  constructor(inputNode: TextureNode, uniforms: BloomUniforms, karis: boolean) {
    super('vec4')
    this.inputNode = inputNode
    this.uniforms = uniforms
    this.karis = karis
    this.outputTexel = uniform(new Vector2(1, 1))
    for (let k = 0; k < BLOOM_LEVELS; k++) this.down.push(makeTarget(`EclipseBloom.down${k}`))
    for (let k = 0; k < BLOOM_LEVELS - 1; k++) this.up.push(makeTarget(`EclipseBloom.up${k}`))
    this.quad.name = 'EclipseBloom'
    // passTexture ties the output texture to this node, so sampling it runs updateBefore() once per frame.
    this.output = passTexture(this as unknown as Parameters<typeof passTexture>[0], this.up[0].texture)
    this.updateBeforeType = NodeUpdateType.FRAME
  }

  /** Sets the mode and the level weights (finest first; `count` = levels rendered). Not per frame. */
  configure(mode: BloomMode, weights: Float32Array, count: number): void {
    this.mode = mode
    this.levels = count
    for (let k = 0; k < BLOOM_LEVELS; k++) this.weights[k] = k < count ? weights[k] : 0
    this.wireUpChain()
  }

  getMode(): BloomMode {
    return this.mode
  }

  getTextureNode(): TextureNode {
    return this.output
  }

  /** Points each upsample at its coarser input and sets its weights for the current level count. */
  private wireUpChain(): void {
    const n = this.levels
    for (let k = 0; k < this.upPasses.length; k++) {
      const p = this.upPasses[k]
      const first = k === n - 2
      p.prev.value = first ? this.down[n - 1].texture : this.up[Math.min(k + 1, BLOOM_LEVELS - 2)].texture
      p.weights.value.set(this.weights[k], first ? this.weights[n - 1] : 1)
    }
    // Texel sizes depend on the wiring: recompute them on the next frame.
    this.sizedMode = null
  }

  private resize(width: number, height: number): void {
    if (width === this.width && height === this.height && this.mode === this.sizedMode) return
    this.width = width
    this.height = height
    this.sizedMode = this.mode
    for (let k = 0; k < BLOOM_LEVELS; k++) {
      const w = bloomLevelSize(this.mode, k, width)
      const h = bloomLevelSize(this.mode, k, height)
      this.down[k].setSize(w, h)
      if (k < BLOOM_LEVELS - 1) this.up[k].setSize(w, h)
    }
    // Prefilter taps step in full-res texels on `full`, two of them on `half` (its level 0 is 1/4 res).
    const tap = this.mode === 'half' ? 2 : 1
    this.inTexel.value.set(tap / width, tap / height)
    for (let k = 0; k < this.downPasses.length; k++) {
      const src = this.down[k]
      this.downPasses[k].texel.value.set(1 / src.width, 1 / src.height)
    }
    const n = this.levels
    for (let k = 0; k < this.upPasses.length; k++) {
      const prev = k === n - 2 ? this.down[n - 1] : this.up[Math.min(k + 1, BLOOM_LEVELS - 2)]
      this.upPasses[k].texel.value.set(1 / prev.width, 1 / prev.height)
    }
    this.outputTexel.value.set(1 / this.up[0].width, 1 / this.up[0].height)
  }

  override updateBefore(frame: NodeFrame): undefined {
    const renderer = frame.renderer
    const pre = this.prefilter
    if (!renderer || this.levels < 2 || !pre) return undefined
    this.state = RendererUtils.resetRendererState(renderer, this.state as RendererState)
    renderer.getDrawingBufferSize(this.size)
    this.resize(this.size.x, this.size.y)
    const quad = this.quad
    const n = this.levels
    // 1. Prefilter: full-res frame -> level 0 (per-tap threshold + Karis average).
    renderer.setRenderTarget(this.down[0])
    quad.material = pre
    quad.render(renderer)
    // 2. Downsample chain: down[k] from down[k - 1].
    for (let k = 1; k < n; k++) {
      renderer.setRenderTarget(this.down[k])
      quad.material = this.downPasses[k - 1].material
      quad.render(renderer)
    }
    // 3. Upsample chain: up[k] = down[k] * w[k] + tent(coarser) * (w[n-1] on the first step, else 1).
    for (let k = n - 2; k >= 0; k--) {
      renderer.setRenderTarget(this.up[k])
      quad.material = this.upPasses[k].material
      quad.render(renderer)
    }
    RendererUtils.restoreRendererState(renderer, this.state)
    return undefined
  }

  override setup(builder: NodeBuilder): Node {
    // getSharedContext() is untyped in @types/three 0.186.
    const shared = context((builder as NodeBuilder & { getSharedContext(): Record<string, unknown> }).getSharedContext())
    const { threshold, smoothWidth, spread } = this.uniforms
    const input = this.inputNode
    const center = uv()
    const material = (name: string, existing: NodeMaterial | undefined, fragment: Node): NodeMaterial => {
      const m = existing ?? new NodeMaterial()
      m.name = name
      m.contextNode = shared
      m.fragmentNode = fragment
      m.needsUpdate = true
      return m
    }

    const inTexel = this.inTexel
    const karis = this.karis
    const preFragment = Fn(() => {
      const tap = (at: Vec2Node): Vec3Node => {
        const c = input.sample(at).rgb
        return c.mul(smoothstep(threshold, threshold.add(smoothWidth), luminance(c)))
      }
      return vec4(thirteenTap(tap, center, inTexel, karis), 1)
    })()
    this.prefilter = material('EclipseBloom_prefilter', this.prefilter ?? undefined, preFragment)

    for (let k = 1; k < BLOOM_LEVELS; k++) {
      const old = this.downPasses[k - 1] as DownPass | undefined
      const src = texture(this.down[k - 1].texture)
      const texel = old?.texel ?? uniform(new Vector2())
      const fragment = Fn(() => vec4(thirteenTap((at) => src.sample(at).rgb, center, texel, false), 1))()
      this.downPasses[k - 1] = { material: material(`EclipseBloom_down${k}`, old?.material, fragment), texel }
    }

    for (let k = 0; k < BLOOM_LEVELS - 1; k++) {
      const old = this.upPasses[k] as UpPass | undefined
      const curr = texture(this.down[k].texture)
      const prev = old?.prev ?? texture(this.up[Math.min(k + 1, BLOOM_LEVELS - 2)].texture)
      const texel = old?.texel ?? uniform(new Vector2())
      const weights = old?.weights ?? uniform(new Vector2(1, 1))
      const fragment = Fn(() => {
        const coarse = tent((at) => prev.sample(at).rgb, center, texel.mul(spread))
        return vec4(curr.sample(center).rgb.mul(weights.x).add(coarse.mul(weights.y)), 1)
      })()
      this.upPasses[k] = { material: material(`EclipseBloom_up${k}`, old?.material, fragment), prev, texel, weights }
    }
    this.wireUpChain()
    return this.output
  }

  override dispose(): void {
    super.dispose()
    for (const rt of this.down) rt.dispose()
    for (const rt of this.up) rt.dispose()
    this.prefilter?.dispose()
    for (const p of this.downPasses) p.material.dispose()
    for (const p of this.upPasses) p.material.dispose()
  }
}
